import { App, ConfigProvider } from 'antd'
import React, { useEffect, useMemo } from 'react'
import { I18nextProvider } from 'react-i18next'
import { Outlet } from 'react-router'

import { DebugBar } from '../../Components/Debug'
import { ResourceCardRenderer } from '../../Components/ResourceCardRenderer'
import { useCurrentRouteMetadata, useCurrentRouteTemplate, useInitializeNavigation } from '../../Hooks'
import { configManager } from '../../Managers/Config'
import { serviceManager } from '../../Managers/Service'
import { NavigationProvider } from '../../Providers/NavigationProvider'
import { getKernelSafe } from '../../Services/KernelRegistry'
import { navigationService } from '../../Services/NavigationService'
import { createLayoutLoadingStore } from './LayoutLoadingStore'
import { LayoutLoadingState } from './LayoutLoadingState'
import { ServiceInitializer } from './ServiceInitializer'

export const Layout: React.FC = () => {
    const kernel = getKernelSafe()
    
    // Всі хуки мають бути викликані ДО будь-яких умовних повернень
    const routeMetadata = useCurrentRouteMetadata()
    const routeTemplate = useCurrentRouteTemplate('default')
    const antdConfig = configManager.get('AntdConfig', {})
    const env = (import.meta as any).env
    
    const loadingStore = useMemo(() => createLayoutLoadingStore(), [])

    // Ініціалізуємо NavigationService з React Router navigate
    useInitializeNavigation()
    
    // useEffect також має бути перед умовним return
    useEffect(() => {
        if (!kernel?.getAllServices) return

        const allKernelServices = kernel.getAllServices()
        const kernelServices = Object.fromEntries(allKernelServices)

        serviceManager.clear()
        serviceManager.sets(kernelServices)

        // Додаємо navigationService до serviceManager
        serviceManager.set('NavigationService', navigationService)
    }, [kernel])

    // Виконуємо loadData якщо він є в шаблоні
    useEffect(() => {
        loadingStore.loadTemplateData(routeTemplate)
    }, [routeTemplate, loadingStore])
    
    useEffect(() => {
        loadingStore.reloadData()
    }, [])
    
    // Мемоізуємо LayoutWrapper щоб уникнути його перестворення
    // ВАЖЛИВО: має бути ДО будь-яких умовних повернень (Rules of Hooks)
    const LayoutWrapper = useMemo<React.FC<{ children: React.ReactNode }>>(() => {
        return ({ children }) => {
            const i18nService = kernel?.getService?.('i18n') as any
            const i18nInstance = i18nService?.i18n
            
            if (!i18nInstance) {
                console.warn('i18n instance not found in kernel')
            }
            
            return (
                <I18nextProvider i18n={i18nInstance}>
                    <ConfigProvider {...antdConfig}>
                        <App>
                            <ServiceInitializer>
                                <NavigationProvider>
                                    {children}
                                    <ResourceCardRenderer />
                                    {env.DEV && <DebugBar />}
                                </NavigationProvider>
                            </ServiceInitializer>
                        </App>
                    </ConfigProvider>
                </I18nextProvider>
            )
        }
    }, [kernel, antdConfig, env.DEV])

    // Якщо Kernel ще не ініціалізовано, нічого не рендеримо
    // clientLoader/loader з root.tsx мають ініціалізувати Kernel перед рендерингом
    if (!kernel) {
        return null
    }

    return (
        <LayoutLoadingState 
            store={loadingStore} 
            routeTemplate={routeTemplate}
            kernel={kernel}
            layoutWrapper={LayoutWrapper}
            routeMetadata={routeMetadata}
            env={env}
        >
            <Outlet />
        </LayoutLoadingState>
    )
}

Layout.displayName = 'Layout'
